import React, { useContext, useEffect, useState } from "react";
import getVariantColor from "helpers/get-variant-color";
import getVariantColorImage from "helpers/get-variant-color-image";
import { PatioContext } from "context/patio-calculator";
import extractGid from "helpers/extract-gid";
import getMetafield from "helpers/get-product-metafields";
import getAdjustedVariants from "helpers/patio-sort";
import Measurement from "./measurement";
import Personalization from "./personalization";
import Customization from "./customization";
import patioData from "./data";
import RadioButton from "../../_components/radio-button";
import ColorSwatch from "../../_components/color-swatch";
import FileUpload from "../../_components/file-upload";
import InputField from "../../_components/input-field";
import { getSelectedOption } from "@root/components/product";
import Button from "@_components/button";
import { CURRENCY } from "@root/utils/constants";
import Money from "@root/components/money";
import { MEASUREMENTS_STATE, VISUAL_SEARCH_EVENT } from "@root/constants/enums";
import ToggleableIcon from "@root/components/toggleable-icon";
import { useLocation } from "@reach/router";
import axios from "axios";
import { getProductType, getProductTier, features as collectionFeatures } from "@root/components/collection/collection-helpers";

function hasPrintSides(customization) {
    return customization?.variants?.length;
}

const FABRIC_CARD_FEATURE_LABEL_OVERRIDES = {
    "Water Proof Protection": "Waterproof",
    "Durability/Thickness": "Durability",
    "Sun/UV Protection": "UV Protection",
};

const PatioConfigurator = ({ product, onVariantChange, quantity, variantColors }) => {
    const location = useLocation();
    const fabric = product?.currentVariant?.node?.selectedOptions[0].value;
    const color = product?.currentVariant?.node?.selectedOptions[1].value;
    const { currentFabricVariants, firstFabricVariants, groupedByFabric } = getAdjustedVariants(product, fabric);
    const measureFile = getMetafield(product, "measuring_instructions", "exactMatch")?.node?.value;
    const { updateConfiguration, customAttributes, setCustomAttributes, pricesByFabric, measurementsState } = useContext(PatioContext);
    const { variantUSP, variantText, fabricCardsContent } = patioData;
    const features = Object.entries(variantText).find((item) => item[0] === fabric.toLowerCase())[1];
    const dynamicCustomizations = product?.customizations;
    const [showFabrics, setShowFabrics] = useState(true);
    const [showUpload, setShowUpload] = useState(false);

    const getFabricCardFeatures = (fabricName) => {
        const baseFeatures = collectionFeatures[getProductType("patio")]?.[getProductTier(fabricName)] ?? [];
        return baseFeatures.map((feature) => ({
            ...feature,
            label: FABRIC_CARD_FEATURE_LABEL_OVERRIDES[feature.label] ?? feature.label,
        }));
    };

    useEffect(() => {
        const trackVisualSearchEvent = async () => {
            if (location.state?.visualSearch?.conversationId) {
                try {
                    await axios.post(`${process.env.GATSBY_AI_URL}/api/v1/product-selection/analytics/send`, {
                        event_name: VISUAL_SEARCH_EVENT.PRODUCT_VISITED,
                        conversation_id: location.state?.visualSearch?.conversationId,
                        product_id: product?.master_product_id,
                        order_id: null
                    });

                    console.log("Visual search event tracked successfully");
                } catch (error) {
                    console.error('Error tracking visual search event:', error);
                }
            }
        };

        trackVisualSearchEvent();
    }, [location.state?.visualSearch?.conversationId, product?.master_product_id]);

    useEffect(() => {
        updateConfiguration({ variant: +extractGid(product?.currentVariant?.node?.id), amount: quantity });
    }, [quantity, product?.currentVariant]);

    let currentIndex = 3;

    // get referenceImages from customAttributes as array
    const defaultReferenceImages = Object.keys(customAttributes)
        .filter((key) => key.startsWith("referenceImage"))
        .map((key) => customAttributes[key]);

    useEffect(() => {
        if (measurementsState === MEASUREMENTS_STATE.FINISHED) {
            setShowFabrics(true);
            setShowUpload(true);
        }
    }, [measurementsState]);

    return (
        <div className="w-full text-blue-700">
            <Measurement
                file={measureFile}
                measurements={product?.measurements}
                updateConfiguration={updateConfiguration}
                product={product}
            />

            <div className="configurator-section">
                <div onClick={() => setShowFabrics(!showFabrics)} className="configurator-section__header">
                    <h3 className="configurator-section__header-title"><span>2. Select fabric</span><span className="flex items-center gap-2">{fabric}<ToggleableIcon state={showFabrics} /> </span></h3>
                </div>

                <div id="fabric-cards" className={`configurator-section__inner configurator-section__inner--toggle ${showFabrics ? "configurator-section__inner--show" : ""}`}>

                    <div className="configurator-section__inner-fabric-cards">
                        {groupedByFabric.map((group) => {
                            const isSelected = group.fabric === fabric;
                            const Badge = fabricCardsContent?.[group.fabric.toLowerCase()]?.badge;
                            const description = fabricCardsContent?.[group.fabric.toLowerCase()]?.description;
                            const isBestSeller = fabricCardsContent?.[group.fabric.toLowerCase()]?.isBestSeller;
                            const warranty = fabricCardsContent?.[group.fabric.toLowerCase()]?.warranty;
                            return (
                                <div onClick={() => {
                                    if (!isSelected) {
                                        onVariantChange(group.variants[0].node.id);
                                    }
                                }} className={`configurator-section__inner-fabric-cards-item ${isSelected ? "configurator-section__inner-fabric-cards-item--active" : ""}`} key={group.fabric}>
                                    <div className="fabric-card__wrapper">
                                        {isBestSeller && (
                                            <div className="fabric-card__best-seller">
                                                Best Seller
                                            </div>
                                        )}
                                        <div className="fabric-card__content">
                                            <div>
                                                <div className="fabric-card__title-logo">
                                                    <h3 className="fabric-card__title">{group.fabric}</h3>

                                                </div>
                                                {/* <div className="configurator-section__inner-fabric-cards-item-price">
                                            </div> */}
                                                <div className="fabric-card__price">
                                                    <Money
                                                        value={{
                                                            currencyCode: CURRENCY,
                                                            amount: pricesByFabric[group.fabric],
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                            <div className="fabric-card__warranty-color-swatch">
                                                <div className="fabric-card__warranty">
                                                    <div className="fabric-card__warranty-badge">
                                                        {Badge}
                                                    </div>
                                                    <p>{warranty}</p>
                                                </div>

                                                <ColorSwatch
                                                    size={"small"}
                                                    baseLabel={isSelected ? 'Color:' : 'Select Color:'}
                                                    key={group.fabric}
                                                    colors={group.variants.map((variant) => ({
                                                        identifier: variant.node.id,
                                                        name: variant.node.selectedOptions[1].value,
                                                        color: getVariantColor(variantColors, variant.node.title.split(" / ")[1].replace(/[/\s]/g, "-").toLowerCase(), variant.node.title.split(" / ")[0].replace(/[/\s]/g, "-"), "patio"),
                                                        image: getVariantColorImage(variantColors, variant.node.title.split(" / ")[1].replace(/[/\s]/g, "-").toLowerCase(), variant.node.title.split(" / ")[0].replace(/[/\s]/g, "-"), "patio"),
                                                    }))}
                                                    value={isSelected ? color : ""}
                                                    onChange={(colorName) => {
                                                        const selectedVariant = group.variants.find((variant) => getSelectedOption(variant.node, "Color") === colorName);
                                                        if (selectedVariant) {
                                                            onVariantChange(selectedVariant.node.id);
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <div className="fabric-card__features">
                                            <div className="fabric-card__features-content">
                                                <div className="fabric-card__description">
                                                    {description}
                                                </div>
                                                <div className="product-item__features product-item__features--vertical">
                                                    {getFabricCardFeatures(group.fabric).map((feature) => (
                                                        <div className="product-item__features-item" key={feature.label}>
                                                            <div className="product-item__features-item-content">
                                                                <p className="product-item__features-item-content-label">{feature.label}</p>
                                                                <div className='product-item__features-progress'>
                                                                    {Array.from({ length: 5 }).map((_, index) => (
                                                                        <span key={index} className={index + 1 <= feature.value ? "progress-bar--active" : "progress-bar--inactive"}></span>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>

                    {/**
                    <div className="configurator-section__inner-fabrics">
                        {firstFabricVariants.map((variant) => (
                            <div className="configurator-section__inner-fabrics-options">
                                <RadioButton
                                    options={[{
                                        label: variant?.node?.title.split(" / ")[0],
                                        value: variant.node.selectedOptions[0].value,
                                    }]}
                                    value={product?.currentVariant?.node?.selectedOptions[0].value}
                                    checked={product?.currentVariant?.node?.selectedOptions[0].value === variant.node.selectedOptions[0].value}
                                    onChange={(value) => onVariantChange({ name: variant.node.selectedOptions[0].name, value })}
                                />
                            </div>
                        ))}
                    </div>

                    <div className="configurator-section__inner-color-swatch">
                        <ColorSwatch
                            colors={currentFabricVariants.map((variant) => ({
                                identifier: variant.node.id,
                                name: variant.node.selectedOptions[1].value,
                                color: getVariantColor(variantColors, variant.node.title.split(" / ")[1].replace(/[/\s]/g, "-").toLowerCase(), variant.node.title.split(" / ")[0].replace(/[/\s]/g, "-"), "patio"),
                                image: getVariantColorImage(variantColors, variant.node.title.split(" / ")[1].replace(/[/\s]/g, "-").toLowerCase(), variant.node.title.split(" / ")[0].replace(/[/\s]/g, "-"), "patio"),
                            }))}
                            value={color}
                            onChange={(colorName) => {
                                const selectedVariant = currentFabricVariants.find((variant) => variant.node.selectedOptions[1].value === colorName);
                                if (selectedVariant) {
                                    onVariantChange({
                                        name: selectedVariant.node.selectedOptions[1].name,
                                        value: selectedVariant.node.selectedOptions[1].value,
                                    });
                                }
                            }}
                        />
                    </div>

                    {variantText && (
                        <div className="configurator-section__inner-variant-features">
                            {features.map((feature) => (
                                <div className="configurator-section__inner-variant-features-items" key={feature?.key}>
                                    <div className="configurator-section__inner-variant-features-items-label">
                                        {feature.key}
                                        :
                                    </div>
                                    <div className="configurator-section__inner-variant-features-items-value">{feature.value}</div>
                                </div>
                            ))}
                        </div>
                    )}

                    {variantUSP && (
                        <div className="configurator-section__inner-variant-usp">
                            {variantUSP.map((usp) => (usp?.text[fabric.toLowerCase()] && (
                                <div className="configurator-section__inner-variant-usp-details" key={usp?.key}>
                                    <div style={{ backgroundImage: `url(${usp?.image})` }} className="configurator-section__inner-variant-usp-details-features" />
                                    <div className="configurator-section__inner-variant-usp-details-features-title">{usp?.key}</div>
                                    <div className="configurator-section__inner-variant-usp-details-features-subtext" dangerouslySetInnerHTML={{ __html: usp?.text[fabric.toLowerCase()] }} />
                                </div>
                            )))}
                        </div>
                    )}
                    **/}

                </div>
            </div>

            {dynamicCustomizations?.map((customization) => {
                if (!hasPrintSides(customization)) return null;

                if (customization.type === "Grid" || customization.type === "Dropdown") {
                    return (
                        <Customization
                            key={customization.id}
                            customizations={[customization]}
                            startIndex={currentIndex++}
                        />
                    );
                }
                if (customization.type === "Personalize") {
                    return (
                        <Personalization
                            key={customization.id}
                            data={customization}
                            bgColor={getVariantColor(
                                variantColors,
                                color.replace(/[/\s]/g, "-").toLowerCase(),
                                fabric.replace(/[/\s]/g, "-").toLowerCase(),
                                "patio",
                            )}
                            index={currentIndex++}
                        />
                    );
                }

                return null;
            })}

            <div className="configurator-section my-4">
                <div onClick={() => setShowUpload(!showUpload)} className="configurator-section__header">
                    <h3 className="configurator-section__header-title">{`${currentIndex}. Upload Reference Image`} </h3>
                    <span className="configurator-section__header-link flex items-center gap-2">Optional <ToggleableIcon state={showUpload} /></span>
                </div>
                <div className={`configurator-section__inner configurator-section__inner--toggle ${showUpload ? "configurator-section__inner--show" : ""}`}>
                    <FileUpload
                        multiple
                        formats={["image/jpeg", "image/png", "image/gif"]}
                        mbLimit={20}
                        defaultValue={defaultReferenceImages}
                        onChange={(files) => {
                            const updatedAttributes = { ...customAttributes };

                            Object.keys(updatedAttributes).forEach((key) => {
                                if (key.includes("referenceImage")) {
                                    delete updatedAttributes[key];
                                }
                            });

                            files.forEach((file, index) => {
                                updatedAttributes[`referenceImage${index === 0 ? "" : index + 1}`] = file;
                            });

                            setCustomAttributes(updatedAttributes);
                        }}
                    />

                    <p className="configurator-section__inner-upload-text">
                        Got a unique shape to cover & want a great fit? Help us with an image, and we will make sure it fits.
                    </p>

                    <h4 className="text-sm mb-2">Any special request for us?</h4>

                    <InputField
                        type="text"
                        value={customAttributes?.specialRequest}
                        onChange={(event) => setCustomAttributes({ ...customAttributes, specialRequest: event.target.value })}
                        placeholder="Enter Product or Brand name to help us serve you better."
                        textarea
                    />
                </div>
            </div>
        </div>
    );
};

export default PatioConfigurator;
